import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { GlobalStyles, ThemeProvider as MuiThemeProvider } from '@mui/material';
import { alpha, createTheme, responsiveFontSizes } from '@mui/material/styles';

export const brandTokens = {
  primary: '#429f41',
  primaryContrast: '#FFFFFF',
  secondary: '#FF7A00',
  success: '#1DD1A1',
  warning: '#FACC15',
};

function buildDarkTheme() {
  const theme = createTheme({
    palette: {
      mode: 'dark',
      primary: { main: brandTokens.primary, contrastText: brandTokens.primaryContrast, dark: '#2f7b2f', light: '#6bc26b' },
      secondary: { main: brandTokens.secondary },
      background: {
        default: '#080F1F',
        paper: 'rgba(17, 25, 40, 0.96)',
      },
      text: {
        primary: '#F8FAFC',
        secondary: 'rgba(226, 232, 240, 0.72)',
      },
      divider: 'rgba(148, 163, 184, 0.24)',
      success: { main: brandTokens.success },
      warning: { main: brandTokens.warning },
    },
    typography: {
      fontFamily: ['"Inter"', '"Roboto"', 'sans-serif'].join(', '),
      h3: { fontWeight: 600, letterSpacing: '-0.02em' },
      h5: { fontWeight: 600, letterSpacing: '-0.01em' },
      body1: { color: 'rgba(226, 232, 240, 0.72)' },
    },
    shape: { borderRadius: 14 },
    transitions: {
      duration: {
        shortest: 0,
        shorter: 0,
        short: 0,
        standard: 0,
        complex: 0,
        enteringScreen: 0,
        leavingScreen: 0,
      },
    },
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiCssBaseline: {
        styleOverrides: () => ({
          '::selection': {
            backgroundColor: alpha(brandTokens.primary, 0.45),
            color: brandTokens.primaryContrast,
          },
          body: { backgroundImage: 'none' },
          '*': { transition: 'none !important', animation: 'none !important' },
          '*::before': { transition: 'none !important', animation: 'none !important' },
          '*::after': { transition: 'none !important', animation: 'none !important' },
        }),
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderColor: 'rgba(148, 163, 184, 0.24)',
            backgroundImage: 'none',
          },
        },
      },
      MuiDialog: {
        defaultProps: { transitionDuration: 0 },
        styleOverrides: {
          paper: {
            backgroundColor: 'rgba(17, 25, 40, 0.96)',
            backgroundImage: 'none',
            border: '1px solid rgba(148, 163, 184, 0.24)',
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: { root: { backgroundColor: 'rgba(0, 0, 0, 0.7)' } },
      },
      MuiPopover: {
        defaultProps: { transitionDuration: 0 },
        styleOverrides: {
          paper: {
            backgroundColor: 'rgba(17, 25, 40, 0.96)',
            backgroundImage: 'none',
            border: '1px solid rgba(148, 163, 184, 0.24)',
            boxShadow: 'none',
          },
        },
      },
      MuiMenu: {
        defaultProps: { transitionDuration: 0 },
        styleOverrides: {
          paper: {
            backgroundColor: 'rgba(17, 25, 40, 0.96)',
            backgroundImage: 'none',
            border: '1px solid rgba(148, 163, 184, 0.24)',
            boxShadow: 'none',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&.Mui-selected, &.Mui-focusVisible, &:hover': { backgroundColor: alpha('#ffffff', 0.06) },
          },
        },
      },
      MuiTabs: { styleOverrides: { indicator: { transition: 'none' } } },
      MuiTab: { styleOverrides: { root: { transition: 'none' } } },
      MuiTooltip: { defaultProps: { enterDelay: 0, leaveDelay: 0, TransitionProps: { timeout: 0 } as any } },
      MuiCollapse: { defaultProps: { timeout: 0 } },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            textTransform: 'none',
            fontWeight: 600,
          },
          containedPrimary: {
            boxShadow: 'none',
          },
          outlined: {
            borderColor: brandTokens.primary,
          },
          outlinedPrimary: {
            borderColor: brandTokens.primary,
            color: brandTokens.primary,
            '&:hover': {
              backgroundColor: alpha(brandTokens.primary, 0.16),
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderColor: 'rgba(148, 163, 184, 0.24)',
            '&.Mui-selected': {
              backgroundColor: brandTokens.primary,
              color: brandTokens.primaryContrast,
            },
          },
        },
      },
    },
  });
  return responsiveFontSizes(theme);
}

function buildLightTheme() {
  const theme = createTheme({
    palette: {
      mode: 'light',
      primary: { main: brandTokens.primary, contrastText: brandTokens.primaryContrast, dark: '#2f7b2f', light: '#6bc26b' },
      secondary: { main: brandTokens.secondary },
      background: {
        default: '#f7f9fb',
        paper: '#ffffff',
      },
      text: {
        primary: '#0B1220',
        secondary: 'rgba(0, 0, 0, 0.64)',
      },
      divider: 'rgba(0, 0, 0, 0.12)',
      success: { main: brandTokens.success },
      warning: { main: brandTokens.warning },
    },
    typography: {
      fontFamily: ['"Inter"', '"Roboto"', 'sans-serif'].join(', '),
      h3: { fontWeight: 600, letterSpacing: '-0.02em' },
      h5: { fontWeight: 600, letterSpacing: '-0.01em' },
    },
    shape: { borderRadius: 14 },
    transitions: {
      duration: {
        shortest: 0,
        shorter: 0,
        short: 0,
        standard: 0,
        complex: 0,
        enteringScreen: 0,
        leavingScreen: 0,
      },
    },
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiCssBaseline: {
        styleOverrides: () => ({
          '::selection': {
            backgroundColor: alpha(brandTokens.primary, 0.28),
            color: brandTokens.primaryContrast,
          },
          body: { backgroundImage: 'none' },
          '*': { transition: 'none !important', animation: 'none !important' },
          '*::before': { transition: 'none !important', animation: 'none !important' },
          '*::after': { transition: 'none !important', animation: 'none !important' },
        }),
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderColor: 'rgba(0, 0, 0, 0.12)',
            backgroundColor: '#ffffff',
          },
        },
      },
      MuiDialog: {
        defaultProps: { transitionDuration: 0 },
        styleOverrides: {
          paper: {
            backgroundColor: '#ffffff',
            backgroundImage: 'none',
            border: '1px solid rgba(0, 0, 0, 0.12)',
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: { root: { backgroundColor: 'rgba(0, 0, 0, 0.5)' } },
      },
      MuiPopover: {
        defaultProps: { transitionDuration: 0 },
        styleOverrides: {
          paper: {
            backgroundColor: '#ffffff',
            backgroundImage: 'none',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            boxShadow: `0 12px 28px ${alpha('#000', 0.16)}`,
          },
        },
      },
      MuiMenu: {
        defaultProps: { transitionDuration: 0 },
        styleOverrides: {
          paper: {
            backgroundColor: '#ffffff',
            backgroundImage: 'none',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            boxShadow: `0 12px 28px ${alpha('#000', 0.16)}`,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&.Mui-selected, &.Mui-focusVisible, &:hover': { backgroundColor: alpha(brandTokens.primary, 0.08) },
          },
        },
      },
      MuiTabs: { styleOverrides: { indicator: { transition: 'none' } } },
      MuiTab: { styleOverrides: { root: { transition: 'none' } } },
      MuiTooltip: { defaultProps: { enterDelay: 0, leaveDelay: 0, TransitionProps: { timeout: 0 } as any } },
      MuiCollapse: { defaultProps: { timeout: 0 } },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 999, textTransform: 'none', fontWeight: 600 },
          containedPrimary: { boxShadow: 'none' },
          outlined: {
            borderColor: brandTokens.primary,
          },
          outlinedPrimary: {
            borderColor: brandTokens.primary,
            color: brandTokens.primary,
            '&:hover': { backgroundColor: alpha(brandTokens.primary, 0.12) },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderColor: 'rgba(0, 0, 0, 0.12)',
            '&.Mui-selected': {
              backgroundColor: brandTokens.primary,
              color: brandTokens.primaryContrast,
            },
          },
        },
      },
    },
  });
  return responsiveFontSizes(theme);
}

export type ThemeMode = 'light' | 'dark';

const ThemeModeContext = createContext<{ mode: ThemeMode; setMode: (m: ThemeMode) => void; toggle: () => void } | null>(null);

export const useThemeMode = () => {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode must be used within ThemeModeProvider');
  return ctx;
};

export const BrandGlobalStyles = (
  <GlobalStyles
    styles={(theme) => ({
      ':root': {
        '--color-brand-primary': theme.palette.primary.main,
        '--color-brand-primary-contrast': theme.palette.primary.contrastText,
        '--color-brand-secondary': theme.palette.secondary.main,
        '--color-surface': theme.palette.background.default,
        '--color-surface-elevated': theme.palette.background.paper,
        '--color-surface-muted': theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.86)' : '#ffffff',
        '--color-text-primary': theme.palette.text.primary,
        '--color-text-secondary': theme.palette.text.secondary,
        '--color-border': theme.palette.divider,
        '--color-success': theme.palette.success.main,
        '--color-warning': theme.palette.warning.main,
      },
      body: {
        backgroundColor: 'var(--color-surface)',
        color: 'var(--color-text-primary)',
      },
    })}
  />
);

export const darkTheme = buildDarkTheme();
export const lightTheme = buildLightTheme();

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => (window?.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'));
  useEffect(() => {
    const stored = localStorage.getItem('travel-theme-mode') as ThemeMode | null;
    if (stored === 'light' || stored === 'dark') setMode(stored);
  }, []);
  useEffect(() => {
    localStorage.setItem('travel-theme-mode', mode);
  }, [mode]);

  const theme = useMemo(() => (mode === 'dark' ? darkTheme : lightTheme), [mode]);
  const value = useMemo(() => ({ mode, setMode, toggle: () => setMode(m => (m === 'dark' ? 'light' : 'dark')) }), [mode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <GlobalStyles styles={{}} />
      <MuiThemeProvider theme={theme as any}>
        {BrandGlobalStyles}
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
};
